import type { StoredGame } from "@onchainreps/storage";

export interface StyleTrait {
  name: string;
  value: number;
  note: string;
}

export interface StyleProfile {
  score: number;
  headline: string;
  traits: StyleTrait[];
  games: number;
}

const SHARP = [
  "sicilian",
  "gambit",
  "scotch",
  "danish",
  "najdorf",
  "dragon",
  "king's indian",
  "kings indian",
  "grunfeld",
  "qgd",
  "queen's gambit declined",
  "caro-kann",
  "caro kann",
  "modern defense",
  "blackmar",
  "budapest",
  "englund",
  "elephant",
  "french",
  "trompowsky",
  "smith-morra",
  "stafford",
  "mieses",
];

const QUIET = [
  "italian",
  "ruy lopez",
  "catalan",
  "english",
  "queen's gambit",
  "queens gambit",
  "nimzo",
  "nimzo-indian",
  "queen's indian",
  "kings indian attack",
  "philidor",
  "classical",
  "slav",
  "petrov",
  "caro-kann classical",
  "reti",
  "bogo",
  "vogand",
  "bird",
];

function clamp(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function matches(opening: string, list: string[]): boolean {
  const lower = opening.toLowerCase();
  return list.some((entry) => lower.includes(entry));
}

function playerAccuracy(game: StoredGame): number | null {
  if (game.playerColor === "white") return game.accuracyWhite;
  if (game.playerColor === "black") return game.accuracyBlack;
  return null;
}

function playerBlunders(game: StoredGame): number | null {
  if (game.playerColor === "white") return game.blundersWhite;
  if (game.playerColor === "black") return game.blundersBlack;
  return null;
}

export function computeStyleProfile(games: StoredGame[]): StyleProfile | null {
  const scored = games.filter((game) => playerAccuracy(game) !== null);
  if (scored.length === 0) return null;

  const accuracies = scored.map((game) => playerAccuracy(game) ?? 0);
  const blunders = scored.map((game) => playerBlunders(game) ?? 0);
  const averageAccuracy = accuracies.reduce((sum, value) => sum + value, 0) / scored.length;
  const averageBlunders = blunders.reduce((sum, value) => sum + value, 0) / scored.length;

  const openings = scored.map((game) => game.opening ?? game.eco ?? "");
  const known = openings.filter((opening) => opening.length > 0);
  const sharp = known.filter((opening) => matches(opening, SHARP)).length;
  const quiet = known.filter((opening) => matches(opening, QUIET)).length;
  const decided = sharp + quiet;
  const risk = decided > 0 ? (sharp / decided) * 100 : 50;

  const accuracyTrait = clamp(averageAccuracy);
  const solidTrait = clamp(100 - averageBlunders * 16 - (100 - averageAccuracy) * 0.7);
  const riskTrait = clamp(risk);

  const score = clamp(averageAccuracy * 0.6 + solidTrait * 0.4);

  const traits: StyleTrait[] = [
    {
      name: "Accuracy",
      value: accuracyTrait,
      note: `${Math.round(averageAccuracy)}% across ${scored.length} game${scored.length === 1 ? "" : "s"}`,
    },
    {
      name: "Solidity",
      value: solidTrait,
      note:
        averageBlunders === 0
          ? "no blunders recorded"
          : `${averageBlunders.toFixed(1)} blunders a game`,
    },
    {
      name: "Risk",
      value: riskTrait,
      note:
        decided === 0
          ? "not enough openings yet"
          : `${sharp} sharp, ${quiet} quiet opening${quiet === 1 ? "" : "s"}`,
    },
  ];

  return {
    score,
    headline: headlineFor({ averageAccuracy, averageBlunders, risk, games: scored.length }),
    traits,
    games: scored.length,
  };
}

function headlineFor(input: {
  averageAccuracy: number;
  averageBlunders: number;
  risk: number;
  games: number;
}): string {
  const sharp = input.risk >= 60;
  const quiet = input.risk <= 35;

  if (input.averageAccuracy >= 85 && input.averageBlunders <= 0.5) {
    return `A ${sharp ? "sharp" : quiet ? "calm" : "balanced"} game with almost nothing to correct.`;
  }
  if (input.averageBlunders >= 2) {
    return `You attack ${sharp ? "hard" : "often"}, but ${input.averageBlunders.toFixed(1)} blunders a game are where the rating goes.`;
  }
  if (input.averageAccuracy >= 75) {
    return `A ${sharp ? "forcing" : quiet ? "positional" : "balanced"} repertoire with solid technique.`;
  }
  return `The foundations are there; the accuracy in ${input.games} game${input.games === 1 ? "" : "s"} is still uneven.`;
}
